import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import AnnouncementBar from "./AnnouncementBar";

const links = [
    { to: "/", label: "Home" },
    { to: "/all", label: "All" },
    { to: "/bundles", label: "Bundles" },
    { to: "/about", label: "About Us" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const { totalQty, setIsOpen } = useCart();
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    return (
        <nav
            className="glass-nav fixed top-0 left-0 right-0 z-50"
            data-testid="main-navbar"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center" data-testid="nav-logo">
                    <img
                        src="https://customer-assets.emergentagent.com/job_peptide-premium/artifacts/xpnt0e9t_PNG%20image.PNG"
                        alt="PepStep"
                        className="h-12 md:h-14 w-auto"
                        style={{ mixBlendMode: "multiply" }}
                    />
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `text-sm font-medium transition-colors ${
                                    isActive
                                        ? "text-pep-primary"
                                        : "text-pep-navy/70 hover:text-pep-navy"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setIsOpen(true)}
                        className="relative w-10 h-10 rounded-full bg-pep-surface hover:bg-pep-navy hover:text-white transition flex items-center justify-center"
                        data-testid="nav-cart-button"
                        aria-label="Cart"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        {totalQty > 0 && (
                            <span
                                data-testid="nav-cart-count"
                                className="absolute -top-1 -right-1 bg-pep-primary text-white text-[10px] font-semibold rounded-full min-w-5 h-5 px-1 flex items-center justify-center"
                            >
                                {totalQty}
                            </span>
                        )}
                    </button>
                    <Link
                        to="/all"
                        className="hidden md:inline-flex items-center gap-2 bg-pep-primary text-white text-sm font-medium rounded-full px-5 py-2.5 hover:bg-pep-navy transition-all hover:-translate-y-0.5 shadow-md"
                        data-testid="nav-shop-now"
                    >
                        Shop Now
                    </Link>
                    <button
                        onClick={() => setMobileOpen((s) => !s)}
                        className="md:hidden w-10 h-10 rounded-full bg-pep-surface flex items-center justify-center"
                        data-testid="nav-mobile-toggle"
                        aria-label="Menu"
                    >
                        {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="md:hidden glass-nav border-t border-pep-border/50" data-testid="nav-mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                className="text-pep-navy font-medium"
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/all"
                            className="bg-pep-primary text-white text-center font-medium rounded-full px-5 py-3"
                            data-testid="nav-mobile-shop-now"
                        >
                            Shop Now
                        </Link>
                    </div>
                </div>
            )}

            <AnnouncementBar />
        </nav>
    );
}
